import firebase from 'firebase/app'
import 'firebase/functions'
import React, { createContext, useEffect, useState } from 'react'
import app from '../base'
import { API, PATH } from '../const'
import { MemberLoginInfo } from '../types/LoginInfo'
import { Redirect, useLocation } from 'react-router-dom'
import { toMemberLoginInfo } from '../mapper/response'
import { PageLoader } from '../components/ui/Loader'

type AuthContextType = {
  login: (email: string, password: string) => Promise<void>
  signInWithNewToken: (token: string) => void
  logout: () => Promise<void>
  revalidate: () => Promise<void>
  bearerToken: string
  loginInfo: MemberLoginInfo
}

const login = async (email: string, password: string): Promise<void> => {
  await app.auth().signInWithEmailAndPassword(email, password)
}

const logout = async (): Promise<void> => {
  try {
    await app.auth().signOut()
  } catch (error) {
    console.error(error)
  }
}

const initialValue: AuthContextType = {
  login,
  logout,
  revalidate: async () => {
    return
  },
  signInWithNewToken: async () => {
    return
  },
  bearerToken: '',
  loginInfo: {
    uid: '',
    user: {
      memberId: '-',
      name: '',
      nameKana: '',
      email: '',
      tel: '',
      postalCode: '',
      address: '',
      activatedAt: '',
      deactivatedAt: '',
      firstLoggedInAt: '',
      family: [],
    },
    office: {
      officeId: '',
      name: '',
      nameKana: '',
      address: '',
    },
  },
}

export const MemberAuthContext = createContext<AuthContextType>(initialValue)

export const MemberAuthProvider: React.FC = (props) => {
  const currentPath = useLocation().pathname

  // フロントで直接firebase authentication を照会して、セッションがなかったらリダイレクトしたいのでここで状態を持つ
  const [loadState, setLoadState] = useState<'loading' | 'noUser' | 'loggedIn'>(
    'loading',
  )
  const [loginInfo, setLoginInfo] = useState<MemberLoginInfo | null>()

  useEffect(() => {
    const unsubscribed = app.auth().onAuthStateChanged(async (user) => {
      setLoginInfo(undefined)
      if (!user) {
        setLoadState('noUser')
      } else {
        setLoadState('loggedIn')
      }
    })
    return () => {
      unsubscribed()
    }
  }, [])

  const [bearerToken, setBearerToken] = useState<string>('')
  const [isOfficeUser, setIsOfficeUser] = useState<boolean>(false)

  const setAboutMe = async () => {
    const token = await firebase.auth().currentUser?.getIdToken()
    if (typeof token === 'undefined') {
      return
    }
    setBearerToken(token)
    const json = await fetch(API.ABOUT_ME, {
      headers: { Authorization: 'Bearer ' + token },
      mode: 'cors',
    }).then((res) => res.json())
    // @todo adminログインしている場合、ここが404になるので/adminにリダイレクトして差し上げたい
    if (!json.user) {
      setIsOfficeUser(true)
    } else {
      setLoginInfo(toMemberLoginInfo(json))
    }
    return
  }

  const signInWithNewToken = (token: string) => {
    firebase.auth().signInWithCustomToken(token)
  }

  useEffect(() => {
    switch (loadState) {
      case 'loading':
        // nothing to do (still undefined)
        break
      case 'noUser':
        setLoginInfo(null)
        break
      case 'loggedIn':
        ;(async () => await setAboutMe())()
        break
      default:
        // https://zenn.dev/suin/scraps/9f3cfc934e98fe#comment-a39a91c1d4a4e9
        throw new Error(`Unexpected state: ${loadState}`)
    }
  }, [loadState])

  if (typeof loginInfo === 'undefined') {
    if (isOfficeUser) {
      return <Redirect to={PATH.OFFICE_TOP} />
    }
    return <PageLoader />
  }

  // ログインしていないユーザの場合
  if (loginInfo === null) {
    if (PATH.NO_LOGIN_PATHS.includes(currentPath)) {
      // ログイン不要の画面にアクセスしてきたらそのまま表示して差し上げる
      return <>{props.children}</>
    } else {
      // ログイン必須画面に非ログインでアクセスしてきたら /login にリダイレクト
      return <Redirect to={PATH.LOGIN} />
    }
  }

  // ログインしてんのに非ログイン前提の画面にアクセスしてきたらトップにリダイレクトする
  if (PATH.NO_LOGIN_PATHS.includes(currentPath)) {
    return <Redirect to={PATH.TOP} />
  }

  return (
    <MemberAuthContext.Provider
      value={{
        login,
        logout,
        signInWithNewToken,
        revalidate: setAboutMe,
        bearerToken,
        loginInfo: loginInfo,
      }}
    >
      {props.children}
    </MemberAuthContext.Provider>
  )
}
